import React, { useEffect, useRef, useState } from 'react';
import { connect, PromiseState } from 'react-refetch';

import useOnClickOutside from 'hooks/useOnClickOutside';
import getSearchURL from 'url-constructors/getSearchURL';
import getBrowseVersionsUrl from 'url-constructors/getBrowseVersionsUrl';

import Box from 'components/elements/Box';
import Span from 'components/elements/span';
import SearchDropdown from './SearchDropdown';

import { IEntity } from 'components/search/ResultsSection/ResultsList/ResultsList';
import { EntityTypes } from 'context/entityContext';
import { IBaseFetchParameters } from 'components/data-containers/BrowseEntities/shared/types';

import { GreenButton } from '../styles';

import colors from 'styles/themes/colors';

type PropsType = {
  isSmallScreen: boolean;
  addedTracks: { id: string; type: 'track' }[];
  addTrackToCustomList: (entityId: string) => void;
  deleteTrack: (entityId: string) => void;
  searchTracks: any;
  foundTracks: PromiseState<IEntity[]>;
  fetchTrackVersions: any;
  trackVersions: PromiseState<any>;
  isSeparate?: boolean;
  addingTrack?: string;
  deletingTrack?: string;
};

const Search: React.FC<PropsType> = ({
  isSmallScreen,
  addedTracks,
  addTrackToCustomList,
  deleteTrack,
  foundTracks = PromiseState.create(),
  searchTracks,
  fetchTrackVersions,
  trackVersions = PromiseState.create(),
  isSeparate = true,
  addingTrack,
  deletingTrack,
}) => {
  const [isOpenSearchDropdown, setIsOpenSearchDropdown] =
    useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [versionsTrack, setShowVersions] = useState<null | string>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const openDropdown = (): void => setIsOpenSearchDropdown(true);

  const showVersions = ({ id }: { id: string | null }): any =>
    setShowVersions(id);

  useOnClickOutside(dropdownRef, () => {
    if (isOpenSearchDropdown) {
      setIsOpenSearchDropdown(false);
    }
  });

  useEffect(() => {
    if (searchQuery.trim()?.length > 0) {
      searchTracks({
        entityType: 'track',
        query: searchQuery,
      });
    }
  }, [searchQuery]);

  useEffect(() => {
    if (versionsTrack) {
      fetchTrackVersions({
        browseEntityType: 'tracks',
        entityType: 'track',
        entityIds: versionsTrack,
        salesType: 'equivalents',
        offset: 0,
      });
    }
  }, [versionsTrack]);

  return (
    <Box
      position={isOpenSearchDropdown ? 'relative' : 'static'}
      margin={
        isSeparate ? '2rem auto' : isSmallScreen ? '1rem auto 0' : 'initial'
      }
      height={isOpenSearchDropdown ? '2.8rem' : 'auto'}
    >
      {!isOpenSearchDropdown && (
        <GreenButton onClick={openDropdown} type="button">
          <Span marginRight={3}>+</Span>
          <Span fontWeight={700}>Add new track</Span>
        </GreenButton>
      )}

      {isOpenSearchDropdown && (
        <Box ref={dropdownRef} backgroundColor={colors.White}>
          <SearchDropdown
            isSmallScreen={isSmallScreen}
            setSearchQuery={setSearchQuery}
            foundTracks={foundTracks}
            addedTracks={addedTracks}
            addTrackToCustomList={addTrackToCustomList}
            showVersions={showVersions}
            versions={trackVersions}
            isSeparate={isSeparate}
            addingTrack={addingTrack}
            deleteTrack={deleteTrack}
            deletingTrack={deletingTrack}
          />
        </Box>
      )}
    </Box>
  );
};

const refetch: any = () => ({
  /** Search tracks for adding */
  searchTracks: (params: {
    entityType: EntityTypes;
    query: string;
  }): { foundTracks: string } => ({
    foundTracks: getSearchURL(params),
  }),

  /** Get track versions and their details */
  fetchTrackVersions: (
    params: IBaseFetchParameters
  ): { trackVersions: string } => ({
    trackVersions: getBrowseVersionsUrl(params),
  }),
});

export default connect<any>(refetch)(Search as any);
